import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { TrendingDown, Zap, Info } from 'lucide-react';

const SERIF = { fontFamily: '"Playfair Display", Georgia, serif' };

// The companies most relevant to customers arriving via a forced switch.
// "Velkommen" is the regulated transition/bankruptcy product itself, so it goes first.
const FOCUS = [
  { match: 'velkommen', label: 'Velkommen', note: 'Overgangsaftale' },
  { match: 'nettopower', label: 'Nettopower' },
  { match: 'power fuel', label: 'Power Fuel' },
  { match: 'vedvar', label: 'Vedvarende' },
];

function matchCompany(byCompetitor, matcher) {
  return byCompetitor.find((c) => (c.name || '').toLowerCase().includes(matcher));
}

/**
 * Shows how much a customer typically saves by moving from their previous /
 * assigned supplier (Velkommen + the named competitors) to enkel energi.
 * Data comes from the live-updated getSavingsComparisonData cache — same source
 * as the front page savings section — so no forcing, just transparent numbers.
 */
export default function VelkommenSavingsComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('ev'); // 'ev' | 'noev'

  useEffect(() => {
    base44.functions
      .invoke('getSavingsComparisonData', {})
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const by = data?.byCompetitor || [];
    return FOCUS.map((f) => {
      const c = matchCompany(by, f.match);
      const ev = Math.round(c?.avgSavings || 0);
      const noev = Math.round(c?.avgSavingsNoEV || 0);
      return { label: f.label, note: f.note, ev, noev };
    }).filter((r) => r.ev > 0 || r.noev > 0);
  }, [data]);

  const avgEV = Math.round(data?.avgWithEVSavings || 0);
  const avgNoEV = Math.round(data?.avgWithoutEVSavings || 0);
  const headline = mode === 'ev' ? avgEV : avgNoEV;

  const maxVal = useMemo(() => {
    const vals = rows.map((r) => (mode === 'ev' ? r.ev : r.noev));
    return vals.length ? Math.max(...vals, 1) : 1;
  }, [rows, mode]);

  const lastUpdated = data?.lastUpdated
    ? new Date(data.lastUpdated).toLocaleDateString('da-DK')
    : null;

  return (
    <section className="py-14 md:py-16 bg-gradient-to-b from-[#1F2E33] to-[#0F1A1D] text-white relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 40%, #99B1BF 0%, transparent 50%), radial-gradient(circle at 80% 60%, #99B1BF 0%, transparent 50%)',
        }}
      />
      <div className="container mx-auto px-6 relative">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full mb-4">
              <TrendingDown className="w-4 h-4 text-[#eab308]" />
              <span className="text-xs font-semibold text-[#eab308] uppercase tracking-wide">Det billigere valg</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={SERIF}>
              Sådan ser vores normale aftaler ud
            </h2>
            <p className="text-[#D9D2C5]/80 text-sm max-w-2xl mx-auto leading-relaxed">
              Du behøver ikke skifte noget – overgangsaftalen kører fint videre. Men vil du have en aftale, der passer bedre
              til din husstand, kan du frit vælge en af vores tre normale aftaler med spotpris uden tillæg. Så billige er de
              typisk sammenlignet med andre selskaber – både med og uden elbil.
            </p>
          </div>

          {/* EV / no-EV toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-white/5 border border-white/15 rounded-full p-1">
              <button
                type="button"
                onClick={() => setMode('ev')}
                className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${
                  mode === 'ev' ? 'bg-[#eab308] text-[#1a2e1a]' : 'text-white/70 hover:text-white'
                }`}
              >
                <Zap className="w-3.5 h-3.5" /> Med elbil
              </button>
              <button
                type="button"
                onClick={() => setMode('noev')}
                className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${
                  mode === 'noev' ? 'bg-[#eab308] text-[#1a2e1a]' : 'text-white/70 hover:text-white'
                }`}
              >
                Uden elbil
              </button>
            </div>
          </div>

          {/* Headline average */}
          <div className="text-center mb-7">
            <p className="text-[#D9D2C5]/60 text-xs mb-1">Typisk årlig besparelse med vores normale aftaler</p>
            {loading ? (
              <div className="h-14 animate-pulse bg-white/10 rounded-xl mx-auto w-56" />
            ) : (
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl md:text-6xl font-bold text-white" style={SERIF}>
                  {headline.toLocaleString('da-DK')}
                </span>
                <span className="text-2xl text-[#eab308] font-bold">kr.</span>
                <span className="text-sm text-white/40">/år</span>
              </div>
            )}
          </div>

          {/* Per-company bars */}
          <div className="bg-white/5 rounded-2xl p-5 border border-[#99B1BF]/25 backdrop-blur-sm">
            <p className="text-[11px] text-[#D9D2C5]/60 mb-4 text-center">
              Sådan ligger vores normale aftaler typisk i forhold til andre selskaber
            </p>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-4 animate-pulse bg-white/10 rounded-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {rows.map((r, i) => {
                  const val = mode === 'ev' ? r.ev : r.noev;
                  const pct = Math.round((val / maxVal) * 82) + 12;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-white text-right flex-shrink-0 w-24 leading-tight">
                        {r.label}
                        {r.note && <span className="block text-[10px] text-[#D9D2C5]/50">{r.note}</span>}
                      </span>
                      <div className="flex-1 h-4 rounded-full overflow-hidden bg-[#99B1BF]/15">
                        <div
                          className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-[#99B1BF] to-[#eab308]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-white w-20 flex-shrink-0 text-right font-semibold">
                        +{val.toLocaleString('da-DK')} kr.
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[10px] text-[#D9D2C5]/45 text-center mt-4 leading-relaxed">
              Beløb er typiske årlige besparelser fra vores prissammenligninger
              {mode === 'ev' ? ' inkl. Saving Mode + laderabat for elbil' : ''}
              {lastUpdated ? ` · opdateret ${lastUpdated}` : ''}. Din egen besparelse afhænger af forbrug og prisområde.
            </p>
          </div>

          <div className="flex items-start gap-2 justify-center mt-5 text-[11px] text-[#D9D2C5]/55 max-w-xl mx-auto">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Det er helt op til dig. Vil du beholde overgangsaftalen, kører den bare videre. Vil du have en aftale, der passer
              bedre til din husstand, kan du vælge en af vores tre normale aftaler nedenfor.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}